import { v4 as uuidV4 } from 'uuid'

import { sendMessage } from '@app/api'
import { runAppInit } from '@app/appInit'
import { getListingIdsFromText } from '@app/components/Chat/components/Dialog/MessageAttachments/MessageListingAttachments/listingLinkUtils'
import { getContextEntity } from '@app/helpers'
import { useConversationActorRole } from '@app/hooks'
import type { MessageAttachment } from '@app/models'
import {
  trackFollowUpNotInterested,
  trackFollowUpSendReminder,
  trackMessagePost,
  trackReplySelectEntitySent,
} from '@app/services/tracking'
import { Queue } from '@app/utils'
import { isImpersonatedB2BSession } from '@app/utils/token/isImpersonatedB2BSession'

import type { ReplySendMessage, UseSendMessageParams } from './types'

const queue = new Queue<() => Promise<void>>()

export const useSendMessage = ({
  conversation,
  currentUser,
  dispatch,
  state,
  utmTags,
  isDeveloper,
  activeFollowUpAction,
}: UseSendMessageParams): ReplySendMessage => {
  const conversationActorRole = useConversationActorRole()

  return async (content: string, attachments: MessageAttachment[]) => {
    if (!conversation?.id) {
      return
    }
    const participant = conversation.participants.find(
      (item) => item.userId === currentUser.id
    )
    if (!participant) {
      return
    }
    const uuid = uuidV4()
    const payloadAttachments = attachments.length ? attachments : undefined

    const pendingSuggestedActionId =
      activeFollowUpAction && activeFollowUpAction !== 'callContact'
        ? conversation.suggestedActions?.find(
          (action) =>
            action.status === 'Pending' &&
              action.toParticipantId === participant.id
        )?.id
        : undefined

    if (isImpersonatedB2BSession()) {
      console.error('Sending messages is blocked for impersonated Staffs')
      await runAppInit()
      return
    }

    dispatch({
      type: 'SEND_MESSAGE_START',
      payload: {
        uuid,
        body: content,
        conversationId: conversation.id,
        fromParticipantId: participant.id,
        attachments: payloadAttachments,
        suggestedActionId: pendingSuggestedActionId,
      },
    })
    const sendingTask = async () => {
      const isReplyFromPushReminder =
        utmTags.utm_push_reply_conversation_id === conversation.id.toString()
      const isReplyFromEmailReminder =
        utmTags.utm_email_reply_conversation_id === conversation.id.toString()

      const replyFrom = isReplyFromPushReminder
        ? 'PushReminder'
        : isReplyFromEmailReminder
          ? 'EmailReminder'
          : undefined

      try {
        const { id, acsChatMessageId } = await sendMessage({
          identity: state.acsChatParticipantId,
          token: state.acsChatToken.token,
          convId: conversation.id,
          message: {
            artificialId: uuid,
            content,
            type: 'Html',
            ReplyFrom: replyFrom,
            suggestedActionId: pendingSuggestedActionId,
            attachments: payloadAttachments,
          },
        })
        const listing = getContextEntity(state.listings, conversation)
        const listingIdsFromLinks = getListingIdsFromText(content)

        trackMessagePost(
          conversation.id,
          listing,
          payloadAttachments?.length ?? 0,
          listingIdsFromLinks.length,
          conversationActorRole
        )

        if (pendingSuggestedActionId && activeFollowUpAction) {
          const normalizedAction = activeFollowUpAction.toLowerCase()

          if (normalizedAction === 'sendreminder') {
            trackFollowUpSendReminder(
              conversation.id,
              listing,
              conversationActorRole
            )
          }

          if (normalizedAction === 'notinterested') {
            trackFollowUpNotInterested(
              conversation.id,
              listing,
              conversationActorRole
            )
          }
        }
        dispatch({
          type: 'SEND_MESSAGE_SUCCESS',
          payload: {
            uuid,
            id,
            acsChatMessageId,
            conversationId: conversation.id,
          },
        })

        if (pendingSuggestedActionId) {
          dispatch({
            type: 'SET_SUGGESTED_ACTION_STATUS',
            payload: {
              conversationId: conversation.id,
              suggestedActionId: pendingSuggestedActionId,
              status: 'Clicked',
            },
          })
        }
        if (payloadAttachments?.length) {
          const selectedListingType = isDeveloper ? 'project' : 'listing'
          payloadAttachments.forEach((attachment) => {
            if (attachment.kind !== 'Listing') {
              return
            }

            trackReplySelectEntitySent(conversation.id, {
              selectedListingId: attachment.listingId,
              selectedListingType,
              listing,
            }, conversationActorRole)
          })
        }
        if (conversation.unread) {
          dispatch({ type: 'OPEN_CONVERSATION', payload: conversation.id })
        }
      } catch (error) {
        dispatch({
          type: 'SEND_MESSAGE_ERROR',
          payload: {
            uuid,
            conversationId: conversation.id,
          },
        })
      }
    }
    queue.push(sendingTask)
  }
}
